import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useMe } from "@/api/hooks";
import { useAssets, useContracts, useTickets } from "@/api/service";
import type { Ref } from "@/api/types";
import { formatDate } from "@/components/ui";
import { money, shortDate } from "@/lib/format";
import { can } from "@/lib/permissions";
import { AddMachine, MachineTable } from "./AssetsPage";
import { NewContract } from "./AmcPages";
import { NewTicketDialog, PriorityBadge, StatusBadge } from "./common";

/** The customer's machines, open tickets and AMC contracts, on the account page. */
export function AccountService({ account }: { account: Ref }) {
  const perms = useMe().data?.permissions ?? [];
  const navigate = useNavigate();
  const machines = useAssets({ account_id: account.id });
  const tickets = useTickets({ account_id: account.id, status: "open" });
  const contracts = useContracts({ account_id: account.id, status: "active" });
  const [dialog, setDialog] = useState<"machine" | "ticket" | "contract" | null>(null);
  if (!can(perms, "service:read")) return null;
  return (
    <section className="card" aria-label="Service">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-3">
        <h2 className="font-semibold">Service</h2>
        <span className="flex gap-2">
          {can(perms, "service:create") && <button type="button" className="btn-ghost text-xs" onClick={() => setDialog("machine")}>Add machine</button>}
          {can(perms, "service:create") && <button type="button" className="btn-ghost text-xs" onClick={() => setDialog("ticket")}>New ticket</button>}
          {can(perms, "amc:manage") && <button type="button" className="btn-ghost text-xs" onClick={() => setDialog("contract")}>New AMC</button>}
        </span>
      </div>
      {contracts.data?.map((c) => (
        <p key={c.id} className="border-b border-line px-5 py-2 text-sm"><Link className="font-medium text-brand hover:underline" to={`/amc/${c.id}`}>{c.number}</Link> active until {shortDate(c.ends_on)}, {money(c.value, c.currency)}
          {c.renewal_due && <span className="ml-2 font-medium text-warn">renewal due</span>}</p>
      ))}
      {tickets.data?.length ? (
        <ul className="divide-y divide-line border-b border-line">{tickets.data.map((t) => (
          <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-2 text-sm">
            <Link to={`/service/${t.id}`} className="hover:text-brand"><b>{t.number}</b> {t.subject}</Link>
            <span className="flex items-center gap-2"><PriorityBadge p={t.priority} /><StatusBadge s={t.status} />{t.due_at && <span className={`text-xs ${t.overdue ? "text-danger" : "text-muted"}`}>{formatDate(t.due_at)}</span>}</span>
          </li>))}</ul>
      ) : null}
      {machines.data?.length ? <MachineTable items={machines.data} /> : <p className="px-5 py-4 text-sm text-muted">No machines recorded for this customer.</p>}
      {dialog === "machine" && <AddMachine account={account} onClose={() => setDialog(null)} />}
      {dialog === "ticket" && <NewTicketDialog account={account} onClose={() => setDialog(null)} onCreated={(t) => navigate(`/service/${t.id}`)} />}
      {dialog === "contract" && <NewContract account={account} onClose={() => setDialog(null)} onCreated={(c) => navigate(`/amc/${c.id}`)} />}
    </section>
  );
}
